import { gql, type TypedDocumentNode } from '@apollo/client';
import type { ComponentProps } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { PickerCard } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';

import type { LeaderboardGroupPicker_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const GridIcon = styled.img`
  width: calc(6 * var(--unit));
  height: calc(6 * var(--unit));
  object-fit: contain;
`;

const Grid = styled(Horizontal).attrs({ gap: 1, center: true })`
  flex-wrap: wrap;
  padding: var(--double-unit);

  @media ${laptopAndAbove} {
    min-width: 460px;
  }
`;

type Props = {
  so5LeaderboardGroups: readonly LeaderboardGroupPicker_so5LeaderboardGroup[];
  selectedGroup?: string;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardGroupPicker = ({
  so5LeaderboardGroups,
  selectedGroup,
  onSelect,
  size = 'sm',
}: Props) => {
  const selectedGroupData = so5LeaderboardGroups.find(
    g => g.slug === selectedGroup
  );

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          size={size}
          icon={
            selectedGroupData?.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedGroupData.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <FormattedMessage defaultMessage="Choose Competition" />
          </PickerHeader>
          <Grid>
            {so5LeaderboardGroups.map(group => (
              <PickerCard
                key={group.slug}
                selected={selectedGroup === group.slug}
                onClick={() => {
                  const slug = group.so5Leaderboards.at(0)?.slug;
                  if (slug) {
                    onSelect(slug);
                    closeDropdown();
                  }
                }}
              >
                {!!group.iconUrl && (
                  <GridIcon src={group.iconUrl} alt={group.displayName ?? ''} />
                )}
              </PickerCard>
            ))}
          </Grid>
        </>
      )}
    </Dropdown>
  );
};

LeaderboardGroupPicker.fragments = {
  so5LeaderboardGroup: gql`
    fragment LeaderboardGroupPicker_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      iconUrl
      displayName
      so5Leaderboards {
        slug
      }
    }
  ` as TypedDocumentNode<LeaderboardGroupPicker_so5LeaderboardGroup>,
};
